import { useCallback, useState } from "react";

// Define a type for the hook's return value
interface UseDownloadReturn {
  isDownloading: boolean;
  downloadError: Error | null;

  // eslint-disable-next-line no-unused-vars
  downloadFile: (url: string, filename: string, token: string) => Promise<void>;
}

export const useDownload = (): UseDownloadReturn => {
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [downloadError, setDownloadError] = useState<Error | null>(null);

  const downloadFile = useCallback(
    async (url: string, filename: string, token: string): Promise<void> => {
      setIsDownloading(true);
      setDownloadError(null);

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Error fetching file: ${response.statusText}`);
        }

        // The response.blob() method returns a Promise that resolves with a Blob
        const blob: Blob = await response.blob();
        const href: string = window.URL.createObjectURL(blob);
        const a = document.createElement("a");

        a.href = href;
        a.download = filename;
        document.body.appendChild(a);
        a.click();

        document.body.removeChild(a);
        window.URL.revokeObjectURL(href);
      } catch (error) {
        console.error("Download failed:", error);
        // Ensure the error is of type Error before setting it
        if (error instanceof Error) {
          setDownloadError(error);
        } else {
          setDownloadError(new Error("An unknown download error occurred."));
        }
      } finally {
        setIsDownloading(false);
      }
    },
    []
  );

  return { downloadFile, isDownloading, downloadError };
};
